import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Confirming your subscription...');
  const pollRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); setMessage('No session found.'); return; }
    const poll = async () => {
      pollRef.current++;
      if (pollRef.current > 15) { setStatus('pending'); setMessage('Payment is being processed. You will receive a confirmation email shortly.'); return; }
      try {
        const { data } = await axios.get(`${API}/billing/checkout-status/${sessionId}`);
        if (data.payment_status === 'paid' || data.status === 'complete') {
          // Also check subscription
          const sub = await axios.get(`${API}/billing/subscription`);
          if (['active', 'trialing'].includes(sub.data.status)) {
            setStatus('success');
            setMessage('Your subscription is active. Welcome to TradePath.');
            return;
          }
        }
        if (data.status === 'expired') { setStatus('error'); setMessage('Session expired. Please try again.'); return; }
      } catch { /* continue polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center px-4" data-testid="billing-success-page">
      <div className="max-w-md text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="h-12 w-12 text-[#0097A7] animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A2533] mb-2">{message}</h1>
            <p className="text-sm text-[#4E6974]">This may take a few seconds...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A2533] mb-2">Subscription active</h1>
            <p className="text-sm text-[#4E6974] mb-6">{message}</p>
            <button onClick={() => navigate('/dashboard')} data-testid="billing-success-dashboard-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#00707C] hover:bg-[#005F6A] text-white text-sm font-medium rounded-lg transition-colors">
              Open the Explore Dashboard <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}
        {status === 'pending' && (
          <>
            <Loader2 className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A2533] mb-2">Payment processing</h1>
            <p className="text-sm text-[#4E6974] mb-6">{message}</p>
            <button onClick={() => navigate('/billing')} className="text-sm text-[#0097A7] hover:underline">Return to billing</button>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="text-xl font-bold text-[#0A2533] mb-2">Something went wrong</h1>
            <p className="text-sm text-[#4E6974] mb-6">{message}</p>
            <button onClick={() => navigate('/billing')} className="text-sm text-[#0097A7] hover:underline">Return to billing</button>
          </>
        )}
      </div>
    </div>
  );
}
